import {
    Form, CheckInput, ListInput, RadioGroupInput, SliderInput,
    FormButton, FormButtonContainer, FormItem, FormSubmitHandler, useForm,
    OptionProps, RadioGroupInputOptionProps,
    DateInput, TimeInput, DateTimeInput, MaskedInput, SelectInput, SwitchInput, TextInput, useAlerts
} from "@sis/react-library"
import { ExampleDto } from "./FormSamplesDifferentControlsPage";
import { Box, ListItemIcon, ListItemText } from "@mui/material";
import ForwardIcon from '@mui/icons-material/Forward';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';

/////////////////////////////////////////////////////////////////////////////////////////////
// Showing the different controls in the library
// you can pass a disabled prop into any control for readonly mode
/////////////////////////////////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////////////////////
// Interface for the form (see basic usage example for more information).
/////////////////////////////////////////////////////////////////////////////////////////////
// allow no members for shorter name to be more readable below
// eslint-disable-next-line
interface T extends ExampleDto { }

interface FormSamplesDifferentControlsFormProps {
    onSubmit: FormSubmitHandler<ExampleDto>;
    submitting: boolean;
}

export const FormSamplesDifferentControlsForm = ({
    onSubmit, submitting
}: FormSamplesDifferentControlsFormProps) => {

    const formName = 'FormWithDifferentControls';

    const selectOptions: OptionProps[] = [
        { value: "Op1", description: "Option 1", filter: "Option 1" },
        { value: "Op2", description: "Option 2", filter: "Option 2" },
        { value: "Op3", description: "Option 3", filter: "Option 3" },
        { value: "Op4", description: "Option 4", filter: "Option 4" }
    ]

    const radioGroupselectOptions: RadioGroupInputOptionProps[] = [
        { value: "Op1", label: "Option 1" },
        { value: "Op2", label: "Option 2", radioprops: { color: "secondary", sx: { color: "blue" } } },
        { value: "Op3", label: "Option 3", labelPlacement: "start" },
        { value: "Op4", label: "Option 4" }
    ]

    const { resetForm } = useForm<T>(formName);
    const { confirm } = useAlerts();

    const handleCancel = () => {
        confirm('Cancel', 'Are you sure you want to cancel and loose your changes?',
            () => { resetForm() },
            "Yes", "No"
        );
    }

    const multipleListItemRender = (option: OptionProps) =>
        <>
            <Box sx={{ width: '26px', paddingTop: '4px' }}><ListItemIcon><ForwardIcon /></ListItemIcon></Box>
            <ListItemText
                sx={{ color: (option.description as string).indexOf('2') > 0 ? 'blue' : undefined }}
            >
                {option.description}
            </ListItemText>
        </>

    return (
        <Form<T>
            name={formName}
            onSubmit={onSubmit}
            submitting={submitting}
            beforeLeaveText='You have made changes, are you sure you want to leave ?'
        >
            <FormItem<T>
                formField="textBox"
                label="Text Box"
                element={
                    // The TextInput accepts any MUI TextFieldProps Props
                    <TextInput
                    //width='100px'  // You can set the width of a textput element                    
                    //loading   // All input elements support a loading. Use this if you want to display that you fetching data or something and this field is waiting to be populated.
                    />
                }
            />
            <FormItem<T>
                formField="date"
                label="Date"
                element={
                    // You can set a minDate, maxDate and format for this date control, uncomment to test                
                    // The DateInput accepts any MUI DatePickerProps Props
                    <DateInput
                    //format='DD/MM/YYYY' 
                    //minDate='2024-08-04'
                    //maxDate='2024-10-04'

                    // You can change actions that appear on the picker
                    //actions={['today']}
                    // For no actions send a blank array
                    //actions={[]}
                    />
                }
            />
            <FormItem<T>
                formField="time"
                label="Time"
                element={
                    // You can set a minTime, maxTime and format for this time control, uncomment to test
                    // The TimeInput accepts any MUI TimePickerProps Props
                    <TimeInput
                    //useDesktopViewForMobileView // The mobile version isn't so friendly, can use desktop version byu setting this prop
                    //format='hh:mm A'
                    //minTime='11:20'
                    //maxTime='18:10'
                    //timeSteps={{hours:1, minutes:5, seconds:5}}  // for 5 minute intervals

                    // You can change actions that appear on the picker
                    //actions={['today']}
                    // For no actions send a blank array
                    //actions={[]}
                    />
                }
            />
            <FormItem<T>
                formField="dateTime"
                label="Date Time"
                element={
                    // You can set a minDate, maxDate, minTime, maxTime and format for this date time control, uncomment to test
                    // The DateTimeInput accepts any MUI DateTimePickerProps Props
                    <DateTimeInput
                    //format='DD/MM/YYYY hh:mm A' 
                    //minDate='2024-08-04'
                    //maxDate='2024-10-04'
                    //minTime='11:20'
                    //maxTime='18:10'
                    //timeSteps={{hours:1, minutes:5, seconds:5}}  // for 5 minute intervals

                    // You can change actions that appear on the picker
                    //actions={['today', 'accept']}
                    // For no actions send a blank array
                    //actions={[]}
                    />
                }
            />
            <FormItem<T>
                formField="singleSelect"
                label="Single Select"
                // The SelectInput accepts any MUI SelectProps Props
                element={<SelectInput options={selectOptions} />}
            />
            <FormItem<T>
                formField="multipleSelect"
                label="Multiple Select"
                element={<SelectInput isMultiSelect options={selectOptions} />}
            />
            <FormItem<T>
                formField="singleList"
                label="Single List"
                // The ListInput accepts any MUI ListProps Props
                element={<ListInput options={selectOptions} />}
            />
            <FormItem<T>
                formField="multipleList"
                label="Multiple List"
                element={
                    <ListInput
                        isMultiSelect
                        options={selectOptions}
                        // You can override the list render if you like. This makes the option blue if it has a 2 in it.
                        itemRender={multipleListItemRender}
                    />
                }
            />
            <FormItem<T>
                formField="radioGroup"
                label="Radio Group"
                // The RadioGroupInput accepts any MUI RadioGroupProps Props
                // The options can be OptionProps or RadioGroupInputOptionProps which extends 
                // FormControlLabelProps if you want to customise each radio option                
                //element={<RadioGroupInput row options={selectOptions} />} // Uncomment to test with OptionProps
                element={<RadioGroupInput row options={radioGroupselectOptions} />}
            />
            <FormItem<T>
                formField="slider"
                label="Slider"
                // The SliderInput accepts any MUI SliderProps Props
                //element={<SliderInput />} // You don't have to have left and right icons
                element={<SliderInput leftIcon={<VolumeDown />} rightIcon={<VolumeUp />} />}
            />
            <FormItem<T>
                formField="switch"
                label="Switch"
                // The SwitchInput accepts any MUI SwitchProps Props
                element={<SwitchInput />}
            />
            <br />
            <FormItem<T>
                formField="check"
                label="Check Box"
                // The CheckInput accepts any MUI CheckboxProps Props
                element={<CheckInput />}
            />
            {/* 
                With the masked input you can either:
                - use an existing masked type (like the first example below)
                - or set your own mask (like the second example below)
             */}
            <FormItem<T>
                formField="maskTelephone"
                label="Mask Phone Number"
                // The MaskedInput accepts any MUI TextFieldProps Props
                element={
                    <MaskedInput
                        maskedType="PhoneNumber"
                    />
                }
            />
            <FormItem<T>
                formField="maskOther"
                label="Mask Other"
                element={
                    <MaskedInput
                        mask="(000) (000)"
                    />}
            />
            <FormButtonContainer>
                <FormButton
                    formButtonText='Cancel'
                    onClick={() => { handleCancel() }}
                />
                <FormButton
                    formButtonText='Submit'
                    submitButton
                />
            </FormButtonContainer>
        </Form>
    )
}